"use client";

import { FC } from "react";
import { dataLayer } from "@repo/analytics/data-layer-utils";
import {
  componentNameConstants,
  dlElementConstants,
  dlEventConstants,
} from "@repo/analytics/data-layer.constants";
import { handleRedirection } from "../../utils/helper";
import { Holding } from "./PortfolioTypes";

interface PortfolioHoldingsProps {
  symbol: string;
  qty: number;
  avg_buy_price: number;
  currentValue: number;
  percentageChange: string;
  plStatus: "positive" | "negative" | "neutral";
  ltp: number;
  todaysChangePercentage: number;
  redirectURL?: string;
  investedValue?: number;
}

const formatSigned = (value: number): string => {
  const fixed = Number(value)?.toFixed(2);
  if (value > 0) return `+${fixed}`;
  if (value < 0) return fixed;
  return fixed;
};

const PortfolioHoldings: FC<PortfolioHoldingsProps> = ({
  symbol,
  qty,
  avg_buy_price,
  currentValue,
  percentageChange,
  plStatus,
  ltp,
  todaysChangePercentage,
  redirectURL,
  investedValue,
}) => {
  const isPnlPositive = plStatus === "positive";
  const pnlPercent = Number(percentageChange);

  const handleClick = () => {
    if (redirectURL) {
      handleRedirection(redirectURL);
    }

    dataLayer(
      "",
      {
        section_title: "Portfolio",
        event: dlEventConstants.CLICK,
        element: dlEventConstants.TAB_CLICK,
        cta_text: symbol ?? "",
        event_type: dlEventConstants.TAB_CLICK,
      },
      {
        EP_PAGE_NAME: componentNameConstants.PORTFOLIO_NAME,
        EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
        EP_CURRENT_VALUE: currentValue.toString() ?? "",
        EP_INVESTED: investedValue?.toString() ?? "",
      },
    );
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className="flex h-[52px] w-full justify-between border-b border-[#E5E7EB] py-[8.5px] min-h-[71px] max-w-[600px] cursor-pointer"
      onClick={handleClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex flex-col flex flex-1 max-w-[302.5px] min-w-[46px] flex-col  gap-[8px]">
        <div className="text-[16px] font-[400px] text-[#1A1A1A]">{symbol}</div>
        <div className="text-sm text-[16px] text-[#666666] font-[400px]">
          {qty} X AVG {Number(avg_buy_price)?.toFixed(2)}
        </div>
      </div>

      <div className="flex flex-col items-end gap-[8px]">
        <div className="flex items-center gap-1 text-[16px] font-medium">
          <span className={isPnlPositive ? "text-green-500" : "text-[#ff1f00]"}>
            {formatSigned(currentValue)} ({formatSigned(pnlPercent)}%)
          </span>
        </div>

        <div className="flex items-center text-[16px] gap-1 text-xs text-[#6B7280]">
          <span>LTP</span>
          <span
            className={`font-[400px] text-[16px] ${isPnlPositive ? "text-green-500" : "text-[#ff1f00]"}`}
          >
            {Number(ltp)?.toFixed(2)} <span className="text-[16px] font-[400px] text-[#666666]">({formatSigned(todaysChangePercentage)})</span>
          </span>
        </div>
      </div>
    </div>
  );
};

interface PortfolioHoldingsComponentProps {
  data: Holding[];
}

export const PortfolioHoldingsComponent = ({ data }: PortfolioHoldingsComponentProps) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  return (
    <>
      <div className="font-[500px] pt-[8px] text-[#1a1a1a] text-[16px]">Your stock investments</div>
      <div className="w-full">
        {data.map((holding: Holding) => {
          return (
            <PortfolioHoldings
              key={holding.symbol}
              symbol={holding.symbol}
              qty={holding.qty}
              avg_buy_price={holding.avg_buy_price}
              currentValue={holding.currentValue}
              percentageChange={holding.percentageChange}
              plStatus={holding.plStatus}
              ltp={holding.ltp}
              todaysChangePercentage={holding.todaysChangePercentage}
              redirectURL={holding.redirectURL}
              investedValue={holding.investedValue}
            />
          );
        })}
      </div>
    </>
  );
};

export default PortfolioHoldings;
